/**
 * The "flush pending writes" seam, standalone.
 *
 * Extracted from serviceWorkerUpdate.ts (which keeps re-exporting it for its
 * existing consumers) so update paths that are shared across all four apps —
 * the appVersion forced-update/banner reload in particular — can flush
 * in-flight edits before a reload WITHOUT importing the CRA-specific
 * service-worker registration that module wraps. Audit finding D3: a reload
 * mid-session can silently discard unsaved survey/form edits; running these
 * handlers first is the mechanism that prevents it.
 *
 * This module knows nothing about saving. Consumers (e.g. the survey capture
 * flow) register the real flush via `registerFlushHandler`; until then the set
 * is empty and `runFlushHandlers` is a harmless no-op.
 */

/** A best-effort flush of pending in-memory edits to durable storage. */
export type FlushHandler = () => void | Promise<void>;

const flushHandlers = new Set<FlushHandler>();

/**
 * Register a handler that flushes pending edits to durable storage. Fires when
 * the app is being hidden/unloaded, before a new worker is activated, and
 * before an appVersion-driven reload. Returns an unsubscribe function.
 *
 * Handlers must be best-effort and non-throwing. On the unload path they cannot
 * be awaited, so durable work must be synchronous or use `navigator.sendBeacon`.
 */
export function registerFlushHandler(handler: FlushHandler): () => void {
  flushHandlers.add(handler);
  return () => {
    flushHandlers.delete(handler);
  };
}

/**
 * Run every registered flush handler, awaiting completion. Never throws — a
 * failing handler must not block the others or the caller (unload/activation).
 */
export async function runFlushHandlers(): Promise<void> {
  const runs = Array.from(flushHandlers, (handler) => {
    try {
      return Promise.resolve(handler());
    } catch {
      return Promise.resolve();
    }
  });
  await Promise.allSettled(runs);
}
